import { useTranslation } from 'react-i18next'
import {
  Award,
  Clock,
  Cog,
  Factory,
  Globe2,
  LifeBuoy,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { Section } from '@/components/Section'
import { usePageSEO } from '@/hooks/usePageSEO'
import { cn } from '@/lib/utils'

interface StrengthItem {
  key: 'experience' | 'multidisciplinary' | 'production' | 'delivery' | 'support' | 'international'
  icon: LucideIcon
}

const STRENGTHS: StrengthItem[] = [
  { key: 'experience', icon: Award },
  { key: 'multidisciplinary', icon: Users },
  { key: 'production', icon: Factory },
  { key: 'delivery', icon: Clock },
  { key: 'support', icon: LifeBuoy },
  { key: 'international', icon: Globe2 },
]

const HISTORY_PILLARS = [
  { valueKey: 'about.historyPillar1Value', labelKey: 'about.historyPillar1Label' },
  { valueKey: 'about.historyPillar2Value', labelKey: 'about.historyPillar2Label' },
  { valueKey: 'about.historyPillar3Value', labelKey: 'about.historyPillar3Label' },
  { valueKey: 'about.historyPillar4Value', labelKey: 'about.historyPillar4Label' },
] as const

const TEAM_ROLES = [
  'technicians',
  'electronics',
  'software',
  'electricians',
  'mechanics',
] as const

const CLIENT_SECTORS = [
  'mechanical',
  'automotive',
  'aerospace',
  'dieMould',
  'tooling',
  'energy',
  'oilGas',
  'heavy',
] as const

export default function AboutPage() {
  const { t } = useTranslation()

  usePageSEO({
    title: t('meta.about.title'),
    description: t('meta.about.description'),
    ogTitle: t('meta.about.ogTitle'),
    ogDescription: t('meta.about.ogDescription'),
    ogImage: '/images/hero/about.jpg',
  })

  return (
    <main id="main-content" className="bg-ink-100">
      {/* ============================================================
          1 · HERO — standardized blueprint PageHeader
          ============================================================ */}
      <PageHeader
        variant="dark"
        eyebrow={t('about.eyebrow')}
        title={t('about.title')}
        subtitle={t('about.headerSubtitle')}
      />

      {/* ============================================================
          2 · HISTORY — editorial split with coordinate-rail pillars
          ============================================================ */}
      <Section tone="muted" spacing="lg" width="default">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT — narrative column */}
          <div className="lg:col-span-7">
            <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-600">
              <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
              {t('about.historyEyebrow')}
            </p>

            <h2 className="fx-text-balance font-bold leading-[1.1] text-ink-900 text-3xl sm:text-4xl md:text-[2.5rem]">
              {t('about.historyTitle')}
            </h2>

            <div className="mt-6 space-y-5 max-w-2xl text-base md:text-lg leading-relaxed text-ink-600 fx-text-pretty">
              <p>{t('about.intro')}</p>
              <p>{t('about.historyBody')}</p>
            </div>

            {/* Decorative coordinate marker */}
            <div
              aria-hidden="true"
              className="mt-10 flex items-center gap-3"
            >
              <span className="block h-[3px] w-16 bg-brand-500" />
              <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-ink-500 tabular-nums">
                {t('home.coordinateMarker')}
              </span>
            </div>
          </div>

          {/* RIGHT — coordinate-rail pillars */}
          <div className="lg:col-span-5">
            <div className="relative border-l-[3px] border-brand-500 pl-6 md:pl-8">
              {/* Top tick */}
              <span
                aria-hidden="true"
                className="absolute -left-[3px] top-0 block h-4 w-[3px] bg-ink-900"
              />
              {/* Bottom tick */}
              <span
                aria-hidden="true"
                className="absolute -left-[3px] bottom-0 block h-4 w-[3px] bg-ink-900"
              />

              <dl className="space-y-8">
                {HISTORY_PILLARS.map((pillar, i) => (
                  <div
                    key={pillar.valueKey}
                    className={cn(
                      'flex items-baseline gap-5',
                      i !== 0 && 'pt-8 border-t border-ink-200'
                    )}
                  >
                    <dt className="shrink-0 font-display text-3xl md:text-[2.25rem] font-bold leading-none tabular-nums text-ink-900">
                      {t(pillar.valueKey)}
                    </dt>
                    <dd className="text-xs md:text-sm uppercase tracking-[0.18em] text-ink-600 font-semibold leading-snug">
                      {t(pillar.labelKey)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </Section>

      {/* ============================================================
          3 · FAVRETTO LEGACY — dark split panel with image + stats
          ============================================================ */}
      <section
        aria-labelledby="about-legacy-title"
        className="relative isolate overflow-hidden bg-ink-900 text-white"
      >
        {/* Diagonal brand stripes bleeding in from the right */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-1/2 md:w-2/5 opacity-[0.09] fx-diagonal-accent"
        />
        {/* Brand glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-32 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-brand-500/20 blur-3xl"
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* LEFT — copy */}
            <div className="lg:col-span-7">
              <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-300">
                <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
                {t('about.legacyEyebrow')}
              </p>

              <h2
                id="about-legacy-title"
                className="fx-text-balance font-bold leading-[1.1] text-white text-3xl sm:text-4xl md:text-[2.5rem]"
              >
                {t('about.legacyTitle')}
              </h2>

              <p className="mt-6 max-w-2xl fx-text-pretty text-base md:text-lg leading-relaxed text-white/80">
                {t('about.legacyBody')}
              </p>

              {/* Stat pills */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  ['about.legacyStat1Value', 'about.legacyStat1Label'],
                  ['about.legacyStat2Value', 'about.legacyStat2Label'],
                ].map(([valueKey, labelKey]) => (
                  <div
                    key={valueKey}
                    className="relative border border-white/10 bg-white/[0.03] p-5 rounded-sm overflow-hidden"
                  >
                    {/* Corner brand tick */}
                    <span
                      aria-hidden="true"
                      className="absolute top-0 left-0 block h-[3px] w-8 bg-brand-500"
                    />
                    <p className="font-display text-2xl md:text-3xl font-bold leading-none tabular-nums text-white">
                      {t(valueKey)}
                    </p>
                    <p className="mt-2.5 text-[11px] md:text-xs uppercase tracking-[0.2em] text-white/65 font-semibold leading-snug">
                      {t(labelKey)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — image tile */}
            <div className="lg:col-span-5">
              <div className="relative">
                {/* Outer brand frame */}
                <div
                  aria-hidden="true"
                  className="absolute -top-3 -left-3 h-14 w-14 border-t-[3px] border-l-[3px] border-brand-500"
                />
                <div
                  aria-hidden="true"
                  className="absolute -bottom-3 -right-3 h-14 w-14 border-b-[3px] border-r-[3px] border-brand-500"
                />
                <div className="relative overflow-hidden rounded-sm border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
                  <img
                    src="/images/hero/about.jpg"
                    alt={t('about.legacyImageAlt')}
                    loading="lazy"
                    className="block h-full w-full object-cover aspect-[4/3]"
                  />
                  {/* Image darken to preserve readability of brand-blue overlay badge */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-tr from-ink-900/40 via-transparent to-brand-900/20 mix-blend-multiply"
                  />
                  {/* Floating brand label */}
                  <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-sm bg-brand-500 px-3 py-1.5 shadow-lg">
                    <span
                      aria-hidden="true"
                      className="block h-1.5 w-1.5 rounded-full bg-white"
                    />
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                      {t('common.brandName')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top + bottom brand rules */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/60 to-transparent"
        />
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-0 right-0 h-px bg-white/10"
        />
      </section>

      {/* ============================================================
          4 · TEAM — multidisciplinary competencies as coordinate chips
          ============================================================ */}
      <Section tone="muted" spacing="lg">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-600">
              <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
              {t('about.teamEyebrow')}
            </p>
            <h2 className="fx-text-balance font-bold leading-[1.1] text-ink-900 text-3xl sm:text-4xl md:text-[2.25rem]">
              {t('about.teamTitle')}
            </h2>
          </div>

          <div className="lg:col-span-7">
            <p className="text-base md:text-lg leading-relaxed text-ink-600 fx-text-pretty">
              {t('about.teamBody')}
            </p>

            {/* Roles label */}
            <p className="mt-10 mb-5 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-ink-500">
              <span aria-hidden="true" className="block h-[2px] w-6 bg-brand-500" />
              {t('about.teamRolesLabel')}
            </p>

            {/* Roles — coordinate-indexed chips */}
            <ul className="flex flex-wrap gap-2.5" role="list">
              {TEAM_ROLES.map((role, i) => (
                <li
                  key={role}
                  className="group relative inline-flex items-stretch overflow-hidden rounded-sm border border-ink-200 bg-white transition-colors duration-200 hover:border-brand-500"
                >
                  {/* Index chip */}
                  <span
                    aria-hidden="true"
                    className="flex items-center justify-center bg-ink-100 px-2.5 font-display text-[11px] font-bold uppercase tracking-[0.15em] text-brand-700 tabular-nums border-r border-ink-200 group-hover:bg-brand-500 group-hover:text-white group-hover:border-brand-500 transition-colors duration-200"
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="flex items-center px-4 py-2.5 text-sm md:text-[15px] font-semibold text-ink-800 group-hover:text-ink-900 transition-colors">
                    {t(`about.teamRoles.${role}`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ============================================================
          5 · STRENGTHS — 6-card grid on base page background
          ============================================================ */}
      <Section
        tone="default"
        spacing="lg"
        eyebrow={t('about.strengthsEyebrow')}
        title={t('about.strengthsTitle')}
      >
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-200 border border-ink-200"
          role="list"
        >
          {STRENGTHS.map((s, i) => {
            const Icon = s.icon
            return (
              <li
                key={s.key}
                className="group relative flex flex-col gap-4 bg-white p-7 md:p-8 transition-colors duration-300 hover:bg-brand-50/60"
              >
                {/* Top tick row */}
                <div
                  aria-hidden="true"
                  className="absolute top-0 left-0 flex gap-1 p-3"
                >
                  <span className="block h-[3px] w-5 bg-brand-500" />
                  <span className="block h-[3px] w-2 bg-brand-500/60" />
                </div>
                {/* Index */}
                <span
                  aria-hidden="true"
                  className="absolute top-3 right-4 text-[10px] font-bold uppercase tracking-[0.25em] text-ink-400 tabular-nums"
                >
                  {String(i + 1).padStart(2, '0')}
                </span>

                <div className="mt-4 flex h-14 w-14 items-center justify-center rounded-sm border border-brand-200 bg-brand-50 text-brand-600 transition-colors duration-300 group-hover:border-brand-500 group-hover:bg-brand-500 group-hover:text-white">
                  <Icon className="h-6 w-6" strokeWidth={2} aria-hidden="true" />
                </div>

                <h3 className="font-bold text-lg md:text-xl leading-snug text-ink-900">
                  {t(`about.strengths.${s.key}`)}
                </h3>

                <p className="text-sm md:text-[15px] leading-relaxed text-ink-600 fx-text-pretty">
                  {t(`about.strengths.${s.key}Desc`)}
                </p>
              </li>
            )
          })}
        </ul>
      </Section>

      {/* ============================================================
          6 · CLIENTS — "Alcuni nostri clienti" — sector taxonomy
          ============================================================ */}
      <Section tone="muted" spacing="lg">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-600">
              <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
              {t('about.clientsTitle')}
            </p>
            <h2 className="fx-text-balance font-bold leading-[1.1] text-ink-900 text-3xl sm:text-4xl md:text-[2.25rem]">
              {t('about.clientsSubtitle')}
            </h2>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <div className="inline-flex items-center gap-3 border border-ink-200 bg-white px-4 py-3 rounded-sm">
              <Cog
                aria-hidden="true"
                className="h-4 w-4 text-brand-600"
                strokeWidth={2.25}
              />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-700">
                {t('about.clientsLabel')}
              </span>
            </div>
          </div>
        </div>

        {/* Sector grid — dense industrial taxonomy */}
        <ul
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-ink-200 border border-ink-200 rounded-sm overflow-hidden"
          role="list"
        >
          {CLIENT_SECTORS.map((sector, i) => (
            <li
              key={sector}
              className="group relative flex items-center gap-3 bg-white px-5 py-5 md:py-6 transition-colors duration-200 hover:bg-brand-50/70"
            >
              {/* Index mark */}
              <span
                aria-hidden="true"
                className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-ink-400 tabular-nums group-hover:text-brand-600 transition-colors"
              >
                {`0${i + 1}`.slice(-2)}
              </span>
              {/* Brand dot */}
              <span
                aria-hidden="true"
                className="block h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0"
              />
              <span className="text-sm md:text-[15px] font-semibold text-ink-800 leading-snug">
                {t(`about.clientsSectors.${sector}`)}
              </span>

              {/* Hover underline */}
              <span
                aria-hidden="true"
                className="absolute bottom-0 left-0 h-[2px] w-0 bg-brand-500 transition-[width] duration-300 group-hover:w-full"
              />
            </li>
          ))}
        </ul>
      </Section>
    </main>
  )
}
